import { z } from 'zod'
import { insightSpecSchema } from './insights'

/**
 * A Coverage Lab conversation, kept on the server (`insight_threads`) so it outlives the browser
 * tab it was started in: the transcript, every version of the chart and how each came about, which
 * one is on screen, and where it was saved. Private to its author — the history list is yours
 * alone — except that a chart saved to a dashboard links its conversation (`threadId` on the
 * block), and anyone who can see that dashboard may READ it, to continue in a thread of their own.
 *
 * The document is the console's own state, validated loosely where it is display (transcript
 * entries carry steps, suggestions, drafted charts) and strictly where it is drawn (every version
 * is an `InsightSpec`). The browser re-parses what it reads back, so an older thread's shapes reach
 * today's renderer filled in.
 */

/** One transcript line; the console's `Entry`. Loose: what each kind carries is display. */
export const insightThreadEntrySchema = z.looseObject({
  id: z.number().int().min(0),
  kind: z.enum(['user', 'assistant', 'system', 'error']),
  text: z.string().max(8000),
})

/** How a version came about: what it changed, and the version it was made from. */
export const insightVersionMetaSchema = z.object({
  /** "Without the Instacart story", "As % share", "Teal bars" — what this version changed. */
  note: z.string().max(160),
  /** The version on screen when this one was made; null for the first. A version made after
   *  stepping back to v3 says so, instead of reading as the next step after the latest. */
  parent: z.number().int().min(0).nullable(),
})
export type InsightVersionMeta = z.infer<typeof insightVersionMetaSchema>

/** One version on a dashboard: the module it is, and on which tab. */
export const insightPlacementSchema = z.object({
  version: z.number().int().min(0),
  tabId: z.string().max(100),
  blockId: z.string().max(100),
})
export type InsightPlacement = z.infer<typeof insightPlacementSchema>

/** The placements of a kept conversation, whichever shape it was kept in. */
export function threadPlacements(doc: {
  placements?: InsightPlacement[] | undefined
  placed?: { tabId: string; blockId: string } | null | undefined
  savedVersion?: number | null | undefined
}): InsightPlacement[] {
  if (doc.placements && doc.placements.length > 0) return doc.placements
  return doc.placed && doc.savedVersion !== null && doc.savedVersion !== undefined
    ? [{ version: doc.savedVersion, ...doc.placed }]
    : []
}

export const INSIGHT_THREAD_MAX_BYTES = 1_000_000
export const INSIGHT_THREAD_MAX_VERSIONS = 200

export const insightThreadDocSchema = z
  .object({
    entries: z.array(insightThreadEntrySchema).max(600),
    versions: z.array(insightSpecSchema).max(INSIGHT_THREAD_MAX_VERSIONS),
    /** One per version, same order. Threads from before versions carried it read as empty. */
    lineage: z.array(insightVersionMetaSchema).max(INSIGHT_THREAD_MAX_VERSIONS).default([]),
    /** The version on screen; -1 = none. */
    active: z.number().int().min(-1),
    nextId: z.number().int().min(0),
    /** Where each version sits on a dashboard: a version can be a module of its own, and the
     *  conversation can have several on the board at once. */
    placements: z.array(insightPlacementSchema).max(INSIGHT_THREAD_MAX_VERSIONS).default([]),
    /** Before placements: the ONE place its chart was saved, and which version. Read (as one
     *  placement) from conversations kept then; never written now. */
    placed: z
      .object({ tabId: z.string().max(100), blockId: z.string().max(100) })
      .nullable()
      .optional(),
    savedVersion: z.number().int().min(0).nullable().optional(),
  })
  .refine(
    (doc) => JSON.stringify(doc).length <= INSIGHT_THREAD_MAX_BYTES,
    'This conversation is too long to keep — start a new one.',
  )
export type InsightThreadDoc = z.infer<typeof insightThreadDocSchema>

/** A row of the history list. */
export const insightThreadSummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  versionCount: z.number().int().min(0),
  updatedAt: z.string(),
})
export type InsightThreadSummary = z.infer<typeof insightThreadSummarySchema>

export const listInsightThreadsResponseSchema = z.object({
  threads: z.array(insightThreadSummarySchema),
})
export type ListInsightThreadsResponse = z.infer<typeof listInsightThreadsResponseSchema>

export const insightThreadSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  doc: insightThreadDocSchema,
  version: z.number().int().min(1),
  updatedAt: z.string(),
  /** False when a teammate's thread is read through a chart they saved: read-only. */
  mine: z.boolean(),
  /** The author's name, for "From Ana's conversation". */
  author: z.string().nullable(),
})
export type InsightThread = z.infer<typeof insightThreadSchema>

/** `PUT /insights/:sub/threads/:id` — the whole thread, conditional on the version last read
 *  (`null` = it is new). A stale write is refused with the thread as it now is. */
export const putInsightThreadSchema = z.object({
  title: z.string().trim().min(1).max(120),
  doc: insightThreadDocSchema,
  version: z.number().int().min(1).nullable(),
})
export type PutInsightThread = z.infer<typeof putInsightThreadSchema>
